import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ItemDetailsForm = ({ formData, onFormDataChange, errors }) => {
  const [noteCharCount, setNoteCharCount] = useState(0);
  const maxNoteLength = 500;

  const conditionOptions = [
    { value: 'great', label: 'Great Condition' },
    { value: 'good', label: 'Good Condition' },
    { value: 'damaged', label: 'Damaged Condition' }
  ];

  const handleInputChange = (field, value) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  const handleNotesChange = (e) => {
    const value = e?.target?.value;
    if (value?.length <= maxNoteLength) {
      setNoteCharCount(value?.length);
      handleInputChange('notes', value);
    }
  };

  const generateLIIN = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000)?.toString()?.padStart(3, '0');
    return `LIIN-${timestamp}-${random}`;
  };

  const handleRegenerateLIIN = () => {
    const newLIIN = generateLIIN();
    handleInputChange('liin', newLIIN);
  };

  useEffect(() => {
    if (!formData?.liin) {
      handleInputChange('liin', generateLIIN());
    }
  }, []);

  useEffect(() => {
    setNoteCharCount(formData?.notes?.length || 0);
  }, [formData?.notes]);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h2 className="text-lg font-semibold text-foreground mb-6">Item Details</h2>
      <div className="space-y-6">
        <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              LaSea Inventory ID Number (LIIN)
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRegenerateLIIN}
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={14}
            >
              Regenerate
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Hash" size={18} color="var(--color-primary)" />
            <code className="text-sm font-mono text-primary font-semibold">
              {formData?.liin || 'Generating...'}
            </code>
          </div>
        </div>

        <Input
          label="Furniture Name"
          type="text"
          placeholder="e.g., Victorian Dining Table, Leather Sofa"
          value={formData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          error={errors?.name}
          required
          description="Enter a descriptive name for the furniture piece"
        />

        <Select
          label="Condition Status"
          options={conditionOptions}
          value={formData?.condition}
          onChange={(value) => handleInputChange('condition', value)}
          placeholder="Select condition"
          error={errors?.condition}
          required
          description="Assess the current condition of the item"
        />

        <Input
          label="Warehouse Arrival Date"
          type="date"
          value={formData?.arrivalDate}
          onChange={(e) => handleInputChange('arrivalDate', e?.target?.value)}
          error={errors?.arrivalDate}
          required
          description="Date when item arrived at warehouse"
          max={new Date()?.toISOString()?.split('T')?.[0]}
        />

        <Select
          label="Warehouse Location"
          options={[
            { value: 'Portland, OR', label: 'Portland, OR' },
            { value: 'Los Angeles, CA', label: 'Los Angeles, CA' }
          ]}
          value={formData?.location}
          onChange={(value) => handleInputChange('location', value)}
          placeholder="Select warehouse"
          error={errors?.location}
          required
          description="Physical location of the item"
        />

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Notes <span className="text-muted-foreground">(Optional)</span>
          </label>
          <textarea
            value={formData?.notes}
            onChange={handleNotesChange}
            placeholder="Add any additional information about the item...\n\nExamples:\n• Dimensions and measurements\n• Special handling instructions\n• Damage details if applicable\n• Client-specific notes"
            className="w-full min-h-[160px] px-4 py-3 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-smooth resize-y"
            maxLength={maxNoteLength}
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              Use multiple lines for detailed information
            </p>
            <span className={`text-xs font-medium ${noteCharCount > maxNoteLength * 0.9 ? 'text-warning' : 'text-muted-foreground'
              }`}>
              {noteCharCount}/{maxNoteLength}
            </span>
          </div>
        </div>

        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm text-foreground font-medium">Required Information:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• At least one image must be captured</li>
                <li>• Furniture name is required</li>
                <li>• Condition status must be selected</li>
                <li>• Arrival date is mandatory</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailsForm;